import { Row } from 'react-bootstrap';
import { Input } from '@progress/kendo-react-inputs';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { useContext, useEffect, useMemo, useState } from 'react';

import { TaskEntity } from '../../../types';
import { TaskContentCard } from './task-content-card';
import { ManualScoring } from '../../../types/manual-scoring';
import { PlayerContextState } from '../../../contexts/player';
import { GridMode, GridToolBar } from '../../grid/grid-tool-bar';
import { GameDocumentContext } from '../../../contexts/game-document';
import { GetResourceValue } from '../../../utils/game-document/resources';
import { TaskContent } from '../../../types/game-document/entities/task-content';
import { GetTaskTitle } from '../../../utils/game-document/assets';
import { GetQuestionsFromTaskContent } from '../../../utils/game-document/assets/task-contents';
import { useDownloadSelected } from '../../../hooks/use-download-selected';

interface TaskGridProps {
  viewMode?: GridMode;
  playerStates?: PlayerContextState[];
  onExport?: () => void;
  onViewChange?: (view: GridMode) => void;
  onSubmitScore?: (e: ManualScoring) => void;
}

export const TaskContentGrid = ({ playerStates, ...props }: TaskGridProps) => {
  const [gameDocument] = useContext(GameDocumentContext);
  const [selectedTaskContent, setSelectedTaskContent] = useState<TaskContent>();
  const [selectedForm, setSelectedForm] = useState<{
    id: string;
    description: string;
  }>();
  const [searchText, setSearchText] = useState<string>('');

  const taskContents = (
    gameDocument.gameDocument?.assets.taskContents ?? []
  ).filter((t) => t.id !== '-' && t.onDemandTaskCategory !== undefined);
  const {
    selectedDownload,
    onResetSelected,
    onDownloadSelected,
    onAddRemoveSelectedDownload
  } = useDownloadSelected(`${gameDocument?.gameCode!}-download-selected.zip`);

  const formQuestion = useMemo(() => {
    if (!selectedTaskContent?.id) return [];

    const question =
      GetQuestionsFromTaskContent(
        gameDocument?.gameDocument!,
        selectedTaskContent.id
      ) ?? [];

    setSelectedForm(question[0]);

    return question;
  }, [selectedTaskContent?.id]);

  useEffect(() => {
    onResetSelected();
  }, [selectedTaskContent, selectedForm]);

  useEffect(() => {
    if (!selectedTaskContent) {
      if (taskContents.length > 0) {
        setSelectedTaskContent(taskContents[0]);
      }
    }
  }, [taskContents]);

  return (
    <>
      <div className={'d-flex mb-3 w-full'}>
        <GridToolBar
          className={'grid-toolbar w-full justify-content-end'}
          showSearch={false}
          showClear={false}
          searchPlaceholder={'Search'}
          columnsToSearch={['taskName', 'question', 'questionType', 'score']}
          onGridModeChange={(e) => {
            if (props.onViewChange) {
              props.onViewChange(e.gridMode);
            }
          }}
          viewMode={props.viewMode}
          handleExport={() => {
            if (props.onExport) {
              props.onExport();
            }
          }}
        />
      </div>
      <div className={'d-flex h-100 flex-grow-1 overflow-hidden'}>
        <div className={'d-flex flex-column group-menu'}>
          <div className={'border-list'}>
            <div className={'p-1 sticky-top bg-white'}>
              <Input
                id={'search-text'}
                type={'text'}
                className={'form-control'}
                placeholder={'Search task..'}
                onChange={(e) =>
                  setSearchText((e.target.value as string) ?? '')
                }
              />
            </div>
            <ul className="list-group facilitator-border-none">
              {taskContents
                .filter((x) => {
                  const taskTitle = GetTaskTitle(
                    gameDocument?.gameDocument,
                    x.id
                  );
                  const searchLower = searchText?.toLowerCase() ?? '';
                  return (
                    taskTitle?.toLowerCase()?.includes(searchLower) ||
                    x.name?.toLowerCase()?.includes(searchLower)
                  );
                })
                .map((item, index) => {
                  return (
                    <li
                      key={index}
                      className="list-group-item cursor-pointer"
                      onClick={() => {
                        setSelectedTaskContent(item);
                        setSelectedForm(undefined);
                      }}>
                      <div className={'d-flex justify-content-between'}>
                        <small
                          className={`${
                            item.id === selectedTaskContent?.id
                              ? 'fw-bold text-dark'
                              : 'text-dark'
                          }`}>
                          {GetTaskTitle(gameDocument?.gameDocument, item.id)}
                        </small>
                      </div>
                    </li>
                  );
                })}
            </ul>
          </div>
        </div>

        <div className={'group-content'}>
          <div className={'ml-5'}>
            <div className={'d-flex justify-content-between'}>
              <span className={'fw-bold'}>
                {GetResourceValue(
                  gameDocument?.gameDocument!,
                  selectedTaskContent?.titleResId!,
                  ''
                )}
              </span>
              {selectedDownload.length > 0 && (
                <span
                  className={'text-primary cursor-pointer'}
                  onClick={onDownloadSelected}>
                  Download selected
                </span>
              )}
            </div>

            {formQuestion.length > 1 && (
              <div className={'d-flex mt-3 flex-column'}>
                <span className={'text-dark'}>Select a question</span>
                <DropDownList
                  data={formQuestion}
                  value={
                    formQuestion?.find((x) => x.id === selectedForm?.id) ?? null
                  }
                  textField={'description'}
                  onChange={(e) => setSelectedForm(e.value)}
                />
              </div>
            )}

            <div className={'mt-4'}>
              <Row>
                {playerStates?.map(
                  (playerState: PlayerContextState, outerIndex: number) => {
                    return playerState?.playerState?.tasks
                      ?.filter((task) => task.id === selectedTaskContent?.id)
                      .map((task: TaskEntity) => {
                        return task?.taskContentFormAnswers
                          ?.filter((x) => x.formId === selectedForm?.id)
                          ?.map((taskContentAnswer, taskContentAnswerIndex) => {
                            return (
                              <TaskContentCard
                                key={taskContentAnswer.formId}
                                taskContentAnswer={taskContentAnswer}
                                index={taskContentAnswerIndex}
                                selectedTaskContent={selectedTaskContent}
                                playerName={playerState?.playerState?.name}
                                playerCode={playerState?.playerState?.code}
                                onSubmitScore={(e) => {
                                  if (props.onSubmitScore) {
                                    props.onSubmitScore(e);
                                  }
                                }}
                                selectedDownload={selectedDownload}
                                onAddRemoveSelectedDownload={
                                  onAddRemoveSelectedDownload
                                }
                              />
                            );
                          });
                      });
                  }
                )}
              </Row>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
